import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MailPlus, KeyRound } from "lucide-react";

export default function AdminInvites() {
  const t = useT();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ["admin-invites"],
    queryFn: async () => (await api.get("/admin/access/invites")).data,
    retry: false,
  });
  const items = Array.isArray(q.data) ? q.data : q.data?.invites || q.data?.items || [];

  const create = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/access/invites", { email: email.trim() });
      setEmail("");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.admin")} title={t("nav.invites")} subtitle={t("admin.subtitle")} />

      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6 flex gap-2">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          placeholder="invitee@example.com"
          className="rounded-xl bg-cream-50 h-11"
          data-testid="admin-invite-email"
        />
        <Button onClick={create} disabled={busy} data-testid="admin-invite-create" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
          <MailPlus className="h-4 w-4 mr-1" /> {t("admin.createInvite")}
        </Button>
      </div>

      {q.isLoading && <LoadingCard />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={KeyRound} title={t("common.empty")} testId="admin-invites-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-invites-list">
        {items.map((iv, i) => (
          <div key={iv.id || iv.token || i} className="rounded-2xl bg-white shadow-card p-4">
            <div className="text-xs text-ink-500">{iv.email || "—"}</div>
            <div className="text-sm text-ink-900 mt-1 font-mono truncate">{iv.token || iv.code || iv.id || "—"}</div>
            {iv.status && <div className="text-xs mt-1 text-sage-700">{iv.status}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
